/**
 * Bannière rouge persistante affichée quand un platform_admin est en mode
 * hotline (impersonation active). Montée tout en haut du DOM via App.tsx.
 *
 * Affiche : « Mode hotline One For All Distribution · Mairie X · Acteur Y · expire dans M:SS · Sortir »
 *
 * Si l'utilisateur clique « Sortir » → exitImpersonation() restaure le token
 * original et le bandeau disparaît. Si le token expire (15 min), l'API
 * renverra 401 sur la prochaine action et l'utilisateur sera renvoyé sur
 * /connexion par le client API.
 *
 * Refonte R3 (mai 2026) :
 *   - Bandeau en glass avec gradient horizontal urgent + glow
 *   - Radio en pulse animé si almostExpired (< 1 min)
 *   - Bouton "Sortir" en Button variant glass cohérent
 *   - aria-live="assertive" conservé pour l'a11y
 */

import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth-context';
import { useNavigate } from 'react-router-dom';
import { LogOut, Radio } from 'lucide-react';

export function ImpersonationBanner() {
  const { impersonation, exitImpersonation } = useAuth();
  const navigate = useNavigate();
  const [now, setNow] = useState(() => new Date());

  // Tic-tac chaque seconde pour l'affichage du compte à rebours
  useEffect(() => {
    if (!impersonation) return;
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [impersonation]);

  if (!impersonation) return null;

  const expiresAt = new Date(impersonation.expiresAt);
  const msLeft = Math.max(0, expiresAt.getTime() - now.getTime());
  const minLeft = Math.floor(msLeft / 60000);
  const secLeft = Math.floor((msLeft % 60000) / 1000);
  const countdown = `${minLeft}:${secLeft.toString().padStart(2, '0')}`;
  const almostExpired = msLeft < 60_000; // < 1 min

  async function handleExit() {
    await exitImpersonation();
    navigate('/platform', { replace: true });
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="sticky top-0 z-50 flex flex-wrap items-center gap-2 border-b border-urgent/40 px-4 py-2 text-sm font-medium text-white backdrop-blur-glass shadow-glow-urgent"
      style={{
        backgroundImage:
          'linear-gradient(90deg, oklch(var(--c-urgent)) 0%, oklch(var(--c-urgent) / 0.78) 50%, oklch(var(--c-urgent) / 0.85) 100%)',
      }}
    >
      <div className="flex items-center gap-2">
        <Radio
          className={`h-4 w-4 shrink-0 ${almostExpired ? 'animate-pulse' : ''}`}
          aria-hidden="true"
        />
        <span className="font-semibold uppercase tracking-r-eyebrow text-xs">
          Mode hotline One For All Distribution
        </span>
      </div>

      <span aria-hidden="true" className="text-white/40">·</span>

      <span className="text-white/90">
        Mairie cible{' '}
        <strong className="text-white">{impersonation.targetTenantNom}</strong>
      </span>

      <span aria-hidden="true" className="text-white/40">·</span>

      <span className="text-white/90">
        Acteur <strong className="text-white">{impersonation.actorEmail}</strong>
      </span>

      <span aria-hidden="true" className="text-white/40">·</span>

      <span className={`tabular-nums text-white/90 ${almostExpired ? 'text-white' : ''}`}>
        Expire dans{' '}
        <strong className={`text-white ${almostExpired ? 'animate-pulse' : ''}`}>{countdown}</strong>
      </span>

      <button
        type="button"
        onClick={handleExit}
        className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-r-md border border-white/25 bg-white/15 px-3 text-xs font-semibold uppercase tracking-r-eyebrow backdrop-blur-glass transition-all hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-urgent"
      >
        <LogOut className="h-3.5 w-3.5" aria-hidden="true" /> Sortir
      </button>
    </div>
  );
}
